import { sendEvent } from "@uri/anomalisa";
import { each } from "gamla";
import { anomalisaToken } from "../../constants.ts";

const renderErrorEvent = "client_render_error";
const uncaughtErrorEvent = "client_error";
const unhandledRejectionEvent = "unhandled_rejection";

const capture = (eventName: string) => {
  sendEvent({ token: anomalisaToken, eventName }).catch(() => {});
};

const ignoredMessageFragments = [
  "window.webkit.messageHandlers",
  "sendDataToNative",
  "Script error.",
  "ResizeObserver loop",
  "Database deleted by request of the user",
  "Connection is closing because of",
  "IO error",
  "Unknown rejection",
  "Unknown error",
  "Not found",
  "AbortError",
  "Java object is gone",
  "Object Not Found Matching Id",
  "Failed to read the 'localStorage' property",
  "reading 'getItem'",
  "reading 'setItem'",
  "reading 'removeItem'",
  "reading 'clear'",
  "localStorage",
  "Access is denied for this document",
  "SecurityError",
  "without an in-progress transaction",
  "Failed to fetch",
  "Load failed",
  "NetworkError when attempting to fetch resource",
  "Failed to connect to MetaMask",
  "MetaMask",
  "web3",
  "The provider is disconnected from all chains",
  "xbrowser",
  "reading 'M_ID'",
  "M_ID",
  "Java exception was raised",
  "The transaction was aborted",
  "Transaction timed out due to inactivity",
  "createObjectStore",
  "signal is aborted",
  "aborted without reason",
];

const errorKeys = [
  "message",
  "error",
  "reason",
  "description",
  "detail",
  "details",
  "title",
  "code",
  "type",
  "name",
] as const;

const isObject = (val: unknown): val is Record<string, unknown> =>
  typeof val === "object" && val !== null;

const safeJsonStringify = (obj: unknown): string => {
  try {
    return JSON.stringify(obj);
  } catch {
    return String(obj);
  }
};

const extractPropertyMessage = (obj: Record<string, unknown>): string => {
  const matchingKey = errorKeys.find((key) => key in obj && Boolean(obj[key]));
  if (!matchingKey) {
    if ("status" in obj && typeof obj.status === "number") {
      const statusText = typeof obj.statusText === "string"
        ? obj.statusText
        : "";
      return statusText ? `${obj.status}: ${statusText}` : String(obj.status);
    }
    return safeJsonStringify(obj);
  }
  const val = obj[matchingKey];
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  return messageOf(val);
};

export const messageOf = (reason: unknown): string =>
  reason instanceof Error
    ? reason.message || reason.name
    : typeof reason === "string"
    ? reason || "Empty error string"
    : typeof reason === "number" || typeof reason === "boolean"
    ? String(reason)
    : reason === null
    ? "null"
    : reason === undefined
    ? "undefined"
    : isObject(reason)
    ? extractPropertyMessage(reason)
    : String(reason) || "Empty error";

const nonActionableExactMessages = new Set([
  "",
  "null",
  "undefined",
  "error",
  "{}",
  "[object object]",
  "unhandledrejection",
  "empty error",
  "empty error string",
  "unknown rejection",
  "unknown error",
]);

export const isIgnorable = (reason: unknown): boolean => {
  if (reason === null || reason === undefined) return true;
  if (isObject(reason) && "isTrusted" in reason) return true;
  const message = messageOf(reason);
  const lowerMsg = message.toLowerCase().trim();
  if (nonActionableExactMessages.has(lowerMsg)) return true;
  return ignoredMessageFragments.some((fragment) =>
    lowerMsg.includes(fragment.toLowerCase())
  );
};

export const errorEventNames =
  (baseEvent: string) => (reason: unknown): string[] =>
    isIgnorable(reason)
      ? []
      : [baseEvent, `${baseEvent}: ${messageOf(reason)}`.slice(0, 120)];

const captureWithMessage = (baseEvent: string) => (reason: unknown) => {
  each(capture)(errorEventNames(baseEvent)(reason));
};

if (typeof globalThis.addEventListener === "function") {
  globalThis.addEventListener(
    "error",
    (event) =>
      captureWithMessage(uncaughtErrorEvent)(event.error ?? event.message),
  );

  globalThis.addEventListener(
    "unhandledrejection",
    (event) => captureWithMessage(unhandledRejectionEvent)(event.reason),
  );
}

export const reportRenderError = captureWithMessage(renderErrorEvent);
